import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ManualReviewButton from './ManualReviewButton';
import ReviewStatusIndicator from './ReviewStatusIndicator';

const CustomerTable = ({ 
  customers, 
  onCustomerSelect, 
  selectedCustomer, 
  onEditCustomer, 
  onSendReviewRequest, 
  onViewReviews, 
  onArchiveCustomer,
  onBulkArchive,
  onDeleteCustomer,
  onRestoreCustomer,
  onBulkRestore,
  onOpenCustomerView,
  loading 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const itemsPerPage = 10;

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer =>
      customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.phone?.includes(searchTerm) ||
      customer.organization_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [customers, searchTerm]);

  const sortedCustomers = useMemo(() => {
    return [...filteredCustomers].sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // Handle date fields specially
      if (sortField === 'created_at' || sortField === 'last_contact_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        
        if (sortDirection === 'asc') {
          return aDate - bDate;
        } else {
          return bDate - aDate;
        }
      }
      
      // String/number comparison for other fields
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
  }, [filteredCustomers, sortField, sortDirection]);

  const paginatedCustomers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedCustomers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedCustomers, currentPage]);

  const totalPages = Math.ceil(sortedCustomers.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRowSelect = (customerId) => {
    setSelectedRows(prev => 
      prev.includes(customerId) 
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const handleSelectAll = () => {
    if (selectedRows.length === paginatedCustomers.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(paginatedCustomers.map(customer => customer.id));
    }
  };

  const getStatusColor = (isArchived, pipelineStage) => {
    if (isArchived) {
      return 'text-text-muted bg-gray-200';
    }
    
    switch (pipelineStage) {
      case 'lead': return 'text-primary bg-primary-50';
      case 'contacted': return 'text-blue-600 bg-blue-50';
      case 'qualified': return 'text-yellow-600 bg-yellow-50';
      case 'proposal': return 'text-purple-600 bg-purple-50';
      case 'negotiation': return 'text-orange-600 bg-orange-50';
      case 'won': return 'text-success bg-success-50';
      case 'lost': return 'text-error bg-error-50';
      default: return 'text-muted-foreground bg-gray-100';
    }
  };

  const getStatusText = (isArchived, pipelineStage) => {
    if (isArchived) return 'Archived';
    
    switch (pipelineStage) {
      case 'lead': return 'Lead';
      case 'contacted': return 'Contacted';
      case 'qualified': return 'Qualified';
      case 'proposal': return 'Proposal Sent';
      case 'negotiation': return 'Negotiation';
      case 'won': return 'Won';
      case 'lost': return 'Lost';
      default: return pipelineStage || 'Unknown';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleBulkArchiveClick = () => {
    if (selectedRows.length === 0) return;
    
    if (window.confirm(`Archive ${selectedRows.length} selected customers?`)) {
      if (onBulkArchive) {
        onBulkArchive(selectedRows, 'Bulk archive');
      } else {
        // Fallback to individual archive calls
        selectedRows.forEach(customerId => onArchiveCustomer(customerId, 'Bulk archive'));
      }
      setSelectedRows([]);
    }
  };

  const handleBulkRestoreClick = () => {
    if (selectedRows.length === 0) return;
    
    if (window.confirm(`Restore ${selectedRows.length} selected customers?`)) {
      if (onBulkRestore) {
        onBulkRestore(selectedRows);
      } else {
        // Fallback to individual restore calls
        selectedRows.forEach(customerId => onRestoreCustomer(customerId));
      }
      setSelectedRows([]);
    }
  };

  // Helper method to determine if a stage should be excluded from estimated value calculations
  const isClosedStage = (stageName) => {
    if (!stageName) return false;
    
    const closedStageKeywords = [
      'lost', 'cancelled', 'canceled', 'closed', 'rejected', 'declined', 
      'abandoned', 'expired', 'withdrawn', 'terminated', 'failed',
      'no-sale', 'no sale', 'dead', 'inactive'
    ];
    
    const normalizedStage = stageName.toLowerCase().trim();
    return closedStageKeywords.some(keyword => normalizedStage.includes(keyword));
  };

  if (loading && customers.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Table Header */}
      <div className="p-4 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
              <Input
                type="search"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
            {selectedRows.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">{selectedRows.length} selected</span>
                <Button variant="outline" size="sm">
                  <Icon name="Mail" size={16} className="mr-2" />
                  Email
                </Button>
                <Button variant="outline" size="sm">
                  <Icon name="Download" size={16} className="mr-2" />
                  Export
                </Button>
                {/* Show Archive or Restore based on selection */}
                {(() => {
                  const selectedCustomers = customers.filter(c => selectedRows.includes(c.id));
                  const hasArchived = selectedCustomers.some(c => c.is_archived);
                  const hasActive = selectedCustomers.some(c => !c.is_archived);
                  
                  return (
                    <>
                      {hasActive && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleBulkArchiveClick}
                          className="text-warning hover:bg-warning-50"
                        >
                          <Icon name="Archive" size={16} className="mr-2" />
                          Archive
                        </Button>
                      )}
                      {hasArchived && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={handleBulkRestoreClick}
                          className="text-success hover:bg-success-50"
                        >
                          <Icon name="RotateCcw" size={16} className="mr-2" />
                          Restore
                        </Button>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            {sortedCustomers.length} customers
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-card-hover">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  checked={selectedRows.length === paginatedCustomers.length && paginatedCustomers.length > 0}
                  onChange={handleSelectAll}
                  className="rounded border-border focus:ring-primary-500"
                />
              </th>
              <th className="text-left p-4 font-medium text-foreground w-72">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 hover:text-primary transition-micro"
                >
                  <span>Customer</span>
                  <Icon 
                    name={sortField === 'name' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Contact</th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('pipeline_stage')}
                  className="flex items-center space-x-1 hover:text-primary transition-micro"
                >
                  <span>Pipeline</span>
                  <Icon 
                    name={sortField === 'pipeline_stage' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('last_contact_date')}
                  className="flex items-center space-x-1 hover:text-primary transition-micro"
                >
                  <span>Last Contact</span>
                  <Icon 
                    name={sortField === 'last_contact_date' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">
                <button
                  onClick={() => handleSort('created_at')}
                  className="flex items-center space-x-1 hover:text-primary transition-micro"
                >
                  <span>Date Added</span>
                  <Icon 
                    name={sortField === 'created_at' ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={14} 
                  />
                </button>
              </th>
              <th className="text-left p-4 font-medium text-foreground">Review Status</th>
              <th className="text-right p-4 font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedCustomers.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                      Loading customers...
                    </div>
                  ) : (
                    'No customers found'
                  )}
                </td>
              </tr>
            ) : (
              paginatedCustomers.map((customer) => (
                <tr 
                  key={customer.id}
                  className={`hover:bg-card-hover transition-micro cursor-pointer ${
                    selectedCustomer?.id === customer.id ? 'bg-primary-50' : ''
                  }`}
                  onClick={() => onCustomerSelect(customer)}
                >
                  <td className="p-4">
                    <input
                      type="checkbox"
                      checked={selectedRows.includes(customer.id)}
                      onChange={() => handleRowSelect(customer.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded border-border focus:ring-primary-500"
                    />
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                        <Icon name={customer.customer_type === 'organization' ? 'Building' : 'User'} size={18} className="text-primary" />
                      </div>
                      <div>
                        <div className="font-medium text-foreground whitespace-nowrap">
                          {customer.name}
                        </div>
                        {customer.customer_type === 'organization' && customer.organization_name && (
                          <div className="text-xs text-text-muted">{customer.organization_name}</div>
                        )}
                        {customer.customer_type === 'organization' && customer.industry && (
                          <div className="text-xs text-text-muted">{customer.industry}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Icon name="Mail" size={14} className="text-text-muted" />
                        <span className="text-sm text-foreground">{customer.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Icon name="Phone" size={14} className="text-text-muted" />
                        <span className="text-sm text-foreground">{customer.phone || 'N/A'}</span>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <div className="space-y-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(customer.is_archived, customer.pipeline_stage)}`}>
                        {getStatusText(customer.is_archived, customer.pipeline_stage)}
                        </span>
                      {customer.estimated_value && (
                        <div className="text-xs text-text-muted mt-1">
                          Est. ${customer.estimated_value.toLocaleString()}
                          {isClosedStage(customer.pipeline_stage) && (
                            <span className="ml-1 text-xs text-error">(Closed)</span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <div className="text-sm text-foreground">
                      {formatDate(customer.last_contact_date)}
                    </div>
                    {customer.next_follow_up && (
                      <div className="text-xs text-text-muted">
                        Next: {formatDate(customer.next_follow_up)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-4">
                    <div className="text-sm text-foreground">
                      {formatDate(customer.created_at)}
                    </div>
                  </td>
                  <td className="py-2 px-4">
                    <ReviewStatusIndicator customerId={customer.id} size="sm" />
                  </td>
                  <td className="py-2 px-4">
                    <div className="flex items-center justify-end space-x-1">
                      {onOpenCustomerView && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOpenCustomerView(customer);
                          }}
                          className="p-2 text-text-muted hover:text-primary"
                          title="Open View"
                        >
                          <Icon name="Eye" size={16} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCustomer(customer);
                        }}
                        className="p-2"
                      >
                        <Icon name="Edit" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSendReviewRequest(customer);
                        }}
                        className="p-2"
                      >
                        <Icon name="MessageSquare" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewReviews(customer);
                        }}
                        className="p-2"
                      >
                        <Icon name="Star" size={16} />
                      </Button>
                      {!customer.is_archived && customer.email && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <ManualReviewButton customer={customer} />
                        </div>
                      )}
                      {!customer.is_archived && onArchiveCustomer && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Archive customer ${customer.name}? This will move them to archived status.`)) {
                              onArchiveCustomer(customer.id, 'Manual archive');
                            }
                          }}
                          className="p-2 text-text-muted hover:text-warning"
                          title="Archive Customer"
                        >
                          <Icon name="Archive" size={16} />
                        </Button>
                      )}
                      {customer.is_archived && onRestoreCustomer && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Restore customer ${customer.name}? This will move them back to active status.`)) {
                              onRestoreCustomer(customer.id);
                            }
                          }}
                          className="p-2 text-text-muted hover:text-success"
                          title="Restore Customer"
                        >
                          <Icon name="RotateCcw" size={16} />
                        </Button>
                      )}
                      {onDeleteCustomer && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteCustomer(customer);
                          }}
                          className="p-2 text-text-muted hover:text-error"
                          title="Delete Permanently"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between p-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, sortedCustomers.length)} of {sortedCustomers.length} customers
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <Icon name="ChevronLeft" size={16} />
            </Button>
            <span className="text-sm text-foreground">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <Icon name="ChevronRight" size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTable;